import { FiCheck, FiChevronRight, FiAlertCircle } from "react-icons/fi";
import { CategoryIcon } from "./CategoryIcon";
import { CategoryIconPicker } from "./CategoryIconPicker";
import { showAppMessage } from "@/components/ui/confirmDialog";
import { useCategoryColors, useCategoryIcons } from "@/hooks/useCategories";
import "../categories.css";
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, ModalActions, TextInput, AppButton } from "@/components/ui/primitives";

interface CategoryData {
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
}

interface NewCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (categoryData: CategoryData, categoryId?: number) => void | Promise<void>;
  category?: Category | null; // Optional category for edit mode
}

export const NewCategoryModal = ({ isOpen, onClose, onSubmit, category }: NewCategoryModalProps) => {
  const { t } = useTranslation(["common", "categories"]);
  const [formData, setFormData] = useState<CategoryData>({
    name: '',
    icon_id: null,
    color_id: null
  });
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [nameError, setNameError] = useState(false);
  const iconsQuery = useCategoryIcons({ enabled: isOpen });
  const colorsQuery = useCategoryColors({ enabled: isOpen });
  const [submitError, setSubmitError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!category;

  // Populate form when editing
  useEffect(() => {
    setSubmitError(false);
    setNameError(false);
    setIsIconPickerOpen(false);
    if (category && isOpen) {
      setFormData({
        name: category.name || '',
        icon_id: category.icon_id ?? null,
        color_id: category.color_id ?? null
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        icon_id: null,
        color_id: null
      });
      setIsSubmitting(false);
    }
  }, [category, isOpen]);

  const handleInputChange = (field: keyof CategoryData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setNameError(true); return; }
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await onSubmit({ ...formData, name: formData.name.trim() }, category?.id);
      showAppMessage({ title: t(isEditMode ? "categories:changesSaved" : "categories:categoryCreated", { name: formData.name.trim() }), tone: "success" });
      onClose();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isEditMode
    ? `${t("common.actions.edit")} ${t("common.labels.category").toLowerCase()}`
    : `${t("common.actions.create")} ${t("common.labels.category").toLowerCase()}`;
  const submitLabel = isEditMode
    ? t("categories:saveChanges")
    : `${t("common.actions.create")} ${t("common.labels.category").toLowerCase()}`;

  const selectedIcon = iconsQuery.data?.find((icon) => icon.id === formData.icon_id);
  const selectedColor = colorsQuery.data?.find((color) => color.id === formData.color_id);
  if (isOpen && isIconPickerOpen) return <CategoryIconPicker value={formData.icon_id} onCancel={() => setIsIconPickerOpen(false)} onSelect={(icon_id) => { setFormData((data) => ({ ...data, icon_id })); setIsIconPickerOpen(false); }} />;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="category-modal-title"
      title={title}
      subtitle={t(isEditMode ? "categories:editSubtitle" : "categories:createSubtitle")}
      onSubmit={handleSubmit}
      noValidate
      mobileSheet
      variant="creation"
      maxWidthClassName="max-w-[560px]"
      panelClassName="category-modal-panel"
      disableClose={isSubmitting}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={submitLabel}
          submittingLabel={t("common.states.creating")}
          onCancel={onClose}
          isSubmitting={isSubmitting}
                  />
      }
    >
      <div className="app-creation-form category-creation-form mx-auto w-full max-w-xl">
        <h3 className="category-form-section">{t("categories.principalSection", { defaultValue: "Principal" })}</h3>
        <FormField label={t("categories:nameLabel")} htmlFor="category-name" required error={nameError ? <><FiAlertCircle className="inline mr-1" />{t("categories:nameRequired")}</> : undefined} errorId="category-name-error">
          <TextInput
            id="category-name"
            type="text"
            value={formData.name}
            onChange={(e) => { handleInputChange('name', e.target.value); setNameError(false); }}
            aria-invalid={nameError}
            aria-describedby={nameError ? 'category-name-error' : undefined}
            placeholder={t("categories:namePlaceholder")}
            required
            disabled={isSubmitting}
            autoFocus
          />
        </FormField>

        <h3 className="category-form-section">{t("categories.customizationSection", { defaultValue: "Personalización" })}</h3>
        <div className="category-preview" aria-live="polite">
          <span className="category-preview-dot" style={{ backgroundColor: selectedColor?.hex, color: selectedColor ? "var(--ds-on-card)" : undefined }}><CategoryIcon name={selectedIcon?.name} size={28} /></span>
          <div><b>{formData.name || t("categories:previewName")}</b><small>{t("categories:previewCaption")}</small></div>
        </div>
        <fieldset disabled={isSubmitting} className="category-color-field">
          <legend className="category-color-label">{t("accounts:accounts.colorLabel", { defaultValue: "Color" })} <span>{t("common.optional", { defaultValue: "Opcional" })}</span></legend>
          {colorsQuery.isLoading ? <p role="status">{t("categories.colorsLoading", { defaultValue: "Cargando colores…" })}</p> : colorsQuery.isError ? <div role="status"><p>{t("categories.colorsLoadError", { defaultValue: "No se pudieron cargar los colores." })}</p><button type="button" className="app-focus-ring underline" onClick={() => colorsQuery.refetch()}>{t("common.actions.retry", { defaultValue: "Reintentar" })}</button></div> : (
            <div className="category-color-options">
              {colorsQuery.data?.map((color) => <label key={color.id} className="category-color-option"><input type="radio" className="peer sr-only" name="category-color" value={color.id} checked={formData.color_id === color.id} onClick={(event) => { if (formData.color_id === color.id) { event.preventDefault(); setFormData((data) => ({ ...data, color_id: null })); } }} onChange={() => setFormData((data) => ({ ...data, color_id: color.id }))} aria-label={t(`accounts:accounts.colorNames.${color.name}`, { defaultValue: color.name })} /><span className="category-color-swatch" style={{ backgroundColor: color.hex }}>{formData.color_id === color.id && <FiCheck />}</span></label>)}
            </div>
          )}
        </fieldset>
        <FormField label={<><span>{t("categories:iconLabel")}</span><span className="app-form-required">{t("common:labels.optional")}</span></>} htmlFor="category-icon-select">
          <AppButton id="category-icon-select" tone="ghost" disabled={isSubmitting} onClick={() => setIsIconPickerOpen(true)} className="category-icon-select">
            <span className="category-icon-select-dot" style={{ backgroundColor: selectedColor?.hex, color: selectedColor ? "var(--ds-on-card)" : undefined }}><CategoryIcon name={selectedIcon?.name} size={18} /></span>
            <span>{selectedIcon?.label ?? t("categories:noIcon")}</span><FiChevronRight aria-hidden="true" />
          </AppButton>
        </FormField>
        {submitError && <p role="alert" className="text-sm text-[var(--ds-negative)]">{t("categories.saveError", { defaultValue: "No se pudo guardar la categoría. Revisa los datos e intenta de nuevo." })}</p>}
      </div>
    </ModalShell>
  );
};
